import React, { useEffect, useRef } from 'react';
import { Play, Target, TrendingUp, Zap, Award, ArrowRight, CheckCircle, BarChart3, Sparkles, Star, Search, Globe, ShoppingCart } from 'lucide-react';
import gsap from 'gsap';
import { Link } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const GoogleAdsPage = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const floatingElementsRef = useRef([]);
  const statsRef = useRef([]);
  const sectionsRef = useRef([]);
  const cardsRef = useRef([]);
  const orbsRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const hero = heroRef.current;
    const floatingElements = floatingElementsRef.current;
    const stats = statsRef.current;
    const sections = sectionsRef.current;
    const cards = cardsRef.current;
    const orbs = orbsRef.current;

    // Floating orbs animation
    orbs.forEach((orb, index) => {
      gsap.to(orb, {
        y: index % 2 === 0 ? -50 : 50,
        x: index % 2 === 0 ? 30 : -30,
        rotation: 360,
        duration: 8 + index * 2,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    });

    // Hero section animation
    gsap.fromTo(hero.querySelector('.hero-content'),
      { opacity: 0, y: 100, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        ease: "power3.out",
        delay: 0.5
      }
    );

    // Floating elements animation
    floatingElements.forEach((element, index) => {
      gsap.to(element, {
        y: -20,
        rotation: 360,
        duration: 4 + index,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
        delay: index * 0.5
      });
    });

    // Stats counter animation
    const animateStats = () => {
      const targets = [8.5, 92, 350, 100];
      stats.forEach((stat, index) => {
        const counter = { value: 0 };
        gsap.to(counter, {
          value: targets[index],
          duration: 2,
          ease: "power2.out",
          onUpdate: () => {
            stat.textContent = targets[index] < 10 ? counter.value.toFixed(1) : Math.floor(counter.value);
          }
        });
      });
    };

    // Stats section scroll trigger
    ScrollTrigger.create({
      trigger: ".stats-section",
      start: "top 80%",
      onEnter: animateStats,
      once: true
    });

    // Sections stagger animation
    sections.forEach((section, index) => {
      gsap.fromTo(section,
        { opacity: 0, y: 80, rotationX: 45 },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Cards animation
    cards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 60, scale: 0.8 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addFloatingRef = (el) => {
    if (el && !floatingElementsRef.current.includes(el)) {
      floatingElementsRef.current.push(el);
    }
  };

  const addStatsRef = (el) => {
    if (el && !statsRef.current.includes(el)) {
      statsRef.current.push(el);
    }
  };

  const addSectionRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const addCardRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const addOrbRef = (el) => {
    if (el && !orbsRef.current.includes(el)) {
      orbsRef.current.push(el);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
          color: "#fff",
          overflow: "hidden",
          paddingTop: "0px",
        }}
      >
        {/* Background Orbs */}
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(66, 133, 244, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "10%",
            right: "-15%",
            filter: "blur(120px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(234, 67, 53, 0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            bottom: "20%",
            left: "-10%",
            filter: "blur(100px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(251, 188, 5, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            filter: "blur(80px)",
            zIndex: 1,
          }}
        />

        {/* Floating Particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(66, 133, 244, 0.1), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(234, 67, 53, 0.1), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(251, 188, 5, 0.1), transparent)
            `,
            backgroundRepeat: "repeat",
            backgroundSize: "150px 100px",
            animation: "particleFloat 20s linear infinite",
            zIndex: 1,
          }}
        />

        {/* Floating Background Elements */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        >
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: "60px",
              height: "60px",
              background: "rgba(66, 133, 244, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4285F4",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(66, 133, 244, 0.2)",
            }}
          >
            <Search size={24} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "30%",
              right: "15%",
              width: "70px",
              height: "70px",
              background: "rgba(234, 67, 53, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#EA4335",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(234, 67, 53, 0.2)",
            }}
          >
            <TrendingUp size={32} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "60%",
              left: "5%",
              width: "65px",
              height: "65px",
              background: "rgba(251, 188, 5, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FBBC05",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(251, 188, 5, 0.2)",
            }}
          >
            <Globe size={28} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "70%",
              right: "20%",
              width: "55px",
              height: "55px",
              background: "rgba(52, 168, 83, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#34A853",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(52, 168, 83, 0.2)",
            }}
          >
            <ShoppingCart size={26} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "40%",
              left: "80%",
              width: "68px",
              height: "68px",
              background: "rgba(66, 133, 244, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4285F4",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(66, 133, 244, 0.2)",
            }}
          >
            <BarChart3 size={30} />
          </div>
        </div>

        {/* Hero Section */}
        <section
          ref={heroRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "8rem 2rem 6rem",
            textAlign: "center",
          }}
        >
          <div
            className="hero-content"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* Hero Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.5rem",
                background: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(66, 133, 244, 0.4)",
                borderRadius: "50px",
                fontSize: "0.95rem",
                fontWeight: "600",
                marginBottom: "3rem",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(66, 133, 244, 0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "-50%",
                  width: "50%",
                  height: "100%",
                  background: "linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
                  transform: "skewX(-20deg)",
                  animation: "shimmer 3s infinite",
                }}
              />
              <Sparkles size={18} style={{ color: "#4285F4" }} />
              Google Ads Excellence
            </div>

            <h1
              style={{
                fontSize: "clamp(3rem, 6vw, 5rem)",
                fontWeight: "900",
                lineHeight: "1.1",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Turn Search Into Sales with  High-Performing
{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #4285F4, #EA4335, #FBBC05, #34A853)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
          Google Ads
              </span>
            </h1>

            <p
              style={{
                fontSize: "1.3rem",
                fontWeight: "600",
                color: "#4285F4",
                marginBottom: "1.5rem",
              }}
            >
              Google Ads Excellence by Beyond the Analysis
            </p>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "900px",
                margin: "0 auto 3rem",
              }}
            >
          Google Ads done right isn’t about spending more money. It’s about making smarter decisions. At Beyond the Analytics, we bring together advanced keyword strategies, precise audience targeting, and continuous optimisation to help your brand stand out in search results. Our goal is to connect you with the right people at the right time and turn those moments into measurable business growth.
            </p>
                   <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #4285F4, #EA4335)",
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 8px 25px rgba(66, 133, 244, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(66, 133, 244, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(66, 133, 244, 0.3)";
              }}
            >
              <Play size={24} />
              Grow with Us
              <ArrowRight size={24} />
            </button>
            </Link> 
          </div>
        </section>

        {/* Stats Section */}
        <section
          className="stats-section"
          style={{
            position: "relative",
            zIndex: 10,
            padding: "4rem 2rem",
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            margin: "0 2rem 6rem",
            maxWidth: "1200px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(66, 133, 244, 0.5), transparent)",
            }}
          />
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "3rem",
              textAlign: "center",
            }}
          >
            {[
              { number: 8.5, label: "Billion Daily Searches", color: "#4285F4" },
              { number: 92, label: "% Market Share", color: "#EA4335" },
              { number: 350, label: "% Average ROAS Increase", color: "#FBBC05" },
              { number: 24, label: " Campaigns Completed", color: "#34A853" },
            ].map((stat, index) => (
              <div key={index} style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "40px",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Star size={20} style={{ color: "#000" }} />
                </div>
                <div
                  ref={addStatsRef}
                  style={{
                    fontSize: "3rem",
                    fontWeight: "900",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    marginBottom: "0.5rem",
                    marginTop: "1rem",
                  }}
                >
                  0
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Google Ads Matter */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(234, 67, 53, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(234, 67, 53, 0.15)",
            }}
          >
            <Target size={18} style={{ color: "#EA4335" }} />
            Strategic Importance
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Why Google Ads{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #EA4335, #FBBC05)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Are Essential
            </span>
          </h2>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "3rem",
              marginBottom: "3rem",
            }}
          >
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                marginBottom: "2rem",
                maxWidth: "800px",
                margin: "0 auto 2rem",
              }}
            >
              Google processes over 8.5 billion searches every day, making it the world's largest source of 
              high-intent traffic. Unlike social media advertising, Google Ads capture customers at the exact 
              moment they're searching for your products or services.
            </p>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "800px",
                margin: "0 auto",
              }}
            >
              With Google's advanced targeting, real-time bidding, and comprehensive analytics, you can reach 
              customers across Search, Display, Shopping, YouTube, and Gmail—turning search intent into revenue 
              with precision and scale.
            </p>
          </div>
                 <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #EA4335, #FBBC05)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(234, 67, 53, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Capture High-Intent Traffic
            <ArrowRight size={20} />
          </button>
          </Link>
        </section>

        {/* Why Google Ads with BTA? */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(66, 133, 244, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(66, 133, 244, 0.15)",
            }}
          >
            <Sparkles size={18} style={{ color: "#4285F4" }} />
            Why Choose BTA
          </div>
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Built to Capture{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #4285F4, #EA4335)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              High-Intent Moments
            </span>
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "2rem",
            }}
          >
            <div
              ref={addCardRef}
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "24px",
                padding: "2.5rem",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: "linear-gradient(135deg, #4285F4, #4285F480)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                  color: "#000",
                }}
              >
                <Target size={28} />
              </div>
              <h3
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "700",
                  marginBottom: "0.75rem",
                  color: "#fff",
                }}
              >
                Precision-Targeted Campaigns
              </h3>
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: "1.7",
                }}
              >
                We don’t just target keywords. We target buyer intent. Using first-party data, competitive analysis, and deep search trend insights, positioning your brand exactly where your ideal customers are looking.
              </p>
            </div>
            <div
              ref={addCardRef}
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "24px",
                padding: "2.5rem",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: "linear-gradient(135deg, #EA4335, #EA433580)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                  color: "#000",
                }}
              >
                <Zap size={28} />
              </div>
              <h3
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "700",
                  marginBottom: "0.75rem",
                  color: "#fff",
                }}
              >
                Smart Bidding with Real-Time Adjustments
              </h3>
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: "1.7",
                }}
              >
                Your ad budget should do more than just spend. With data-driven bidding strategies that adjust in real time, your campaigns are always working smarter and helping you get better results with every click.
              </p>
            </div>
            <div
              ref={addCardRef}
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "24px",
                padding: "2.5rem",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: "linear-gradient(135deg, #FBBC05, #FBBC0580)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                  color: "#000",
                }}
              >
                <BarChart3 size={28} />
              </div>
              <h3
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "700",
                  marginBottom: "0.75rem",
                  color: "#fff",
                }}
              >
                Structured for Scale
              </h3>
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: "1.7",
                }}
              >
                Whether you're running local campaigns or expanding globally, you get a scalable ad setup built to grow with your business while keeping performance high and control in your hands.
              </p>
            </div>
          </div>
                <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #4285F4, #EA4335)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              marginTop: "4rem",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(66, 133, 244, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Unlock Your Growth
            <ArrowRight size={20} />
          </button>
       </Link>
        </section>

        {/* BTA Services Section */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(251, 188, 5, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(251, 188, 5, 0.15)",
            }}
          >
            <Award size={18} style={{ color: "#FBBC05" }} />
            Premium Services
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1rem",
              color: "#fff",
            }}
          >
            Beyond the Analytics{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FBBC05, #4285F4)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Google Ads Services
            </span>
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginBottom: "4rem",
            }}
          >
            {[
              "Smart campaign setup and precision account structuring",
              "Multi-format campaigns across Search, Display, YouTube, and Gmail",
              "In-depth competitor analysis and intelligent audience targeting",
              "Intent-driven keyword planning and persuasive ad creation",
              "Performance-led bid strategies and budget optimisation",
              "Conversion-focused call tracking and lead quality analysis",
              "Continuous A/B testing for agile performance refinement",
              "Conversion rate optimisation and strategic consulting",
            ].map((achievement, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1.5rem",
                  background: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.transform = "translateX(10px)";
                  e.currentTarget.style.borderColor = "rgba(251, 188, 5, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #FBBC05, #4285F4)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle size={20} style={{ color: "#000" }} />
                </div>
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {achievement}
                </span>
              </div>
            ))}
          </div>
                <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #FBBC05, #4285F4)",
              color: "#000",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(251, 188, 5, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Access Your Free Growth Plan
            <ArrowRight size={20} />
          </button>
</Link>
        </section>
        
        {/* How BTA Drives Growth Section */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "3rem",
            }}
          >
            <h2
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              How BTA Drives Growth with Google Ads
            </h2>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                marginBottom: "2rem",
                maxWidth: "900px",
                margin: "0 auto 2rem",
              }}
            >
              You need more than ads. You need results. That’s why our Google Ads approach is built to turn customer intent into real business impact. From search and display to video and shopping campaigns, your brand appears where it matters most, in front of the right people at the right time, ready to take action.
            </p>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "900px",
                margin: "0 auto",
              }}
            >
              We begin by understanding your business, your audience, and your goals. Instead of just focusing on keywords, we build campaigns that follow your customer journey from start to finish. With clear communication and regular updates, we track performance closely, test what works, and make improvements along the way.
            </p>
          </div>
        </section>

        {/* Performance Step by Step */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(52, 168, 83, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(52, 168, 83, 0.15)",
            }}
          >
            <Zap size={18} style={{ color: "#34A853" }} />
            Our Blueprint
          </div>
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Performance, Step by Step
          </h2>
          <p
            style={{
              fontSize: "1.2rem",
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: "4rem",
            }}
          >
            Our 6-step blueprint for your success
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "2rem",
              marginBottom: "3rem",
            }}
          >
            {[
              {
                icon: Search,
                title: "Discovery & Alignment",
                description: "We kick off with a deep discovery process to understand your business, audience, goals, and current performance. Every insight gathered fuels a smarter, more aligned campaign strategy.",
                color: "#4285F4",
              },
              {
                icon: BarChart3,
                title: "Campaign Architecture",
                description: "Our team sets up a data-backed foundation with precise conversion tracking, strategic audience targeting, and full-funnel campaign builds tailored to your goals.",
                color: "#EA4335",
              },
              {
                icon: CheckCircle,
                title: "Pre-Launch Review",
                description: "Before going live, we walk you through every element of your campaigns. We ensure full alignment, clarity, and readiness — no surprises, just strategy.",
                color: "#FBBC05",
              },
              {
                icon: Globe,
                title: "Data Collection & Learning",
                description: "Once live, we enter a learning phase to monitor performance trends, audience behaviour, and search intent. This insight shapes the next phase of growth.",
                color: "#34A853",
              },
              {
                icon: TrendingUp,
                title: "Optimisation & Refinement",
                description: "With real-time data in hand, we fine-tune bids, creatives, keywords, and placements. Every adjustment is made to boost efficiency and maximise ROI.",
                color: "#4285F4",
              },
              {
                icon: Award,
                title: "Scaling with Precision",
                description: "We don’t just scale spend — we scale smart. Factoring in seasonality, trends, and emerging opportunities, we expand campaigns while protecting performance and profitability.",
                color: "#EA4335",
              },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.5rem",
                    padding: "2rem",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.transform = "translateX(10px)";
                    e.currentTarget.style.borderColor = `${item.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      background: `linear-gradient(135deg, ${item.color}, ${item.color}80)`,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#000",
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent size={28} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "700",
                        marginBottom: "0.5rem",
                        color: "#fff",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        color: "rgba(255, 255, 255, 0.7)",
                        lineHeight: "1.6",
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Final CTA */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "32px",
              padding: "4rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "2px",
                background: "linear-gradient(90deg, transparent, rgba(66, 133, 244, 0.8), rgba(234, 67, 53, 0.8), transparent)",
              }}
            />

            <h2
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Turn Clicks into Customers
            </h2>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "700px",
                margin: "0 auto 3rem",
              }}
            >
              Get a free 1:1 session to spot quick wins and boost performance.
            </p>
            <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #4285F4, #EA4335, #FBBC05, #34A853)",
                backgroundSize: "200% 100%",
                color: "#000",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 10px 30px rgba(66, 133, 244, 0.3)",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(66, 133, 244, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(66, 133, 244, 0.3)";
              }}
            >
              <Play size={24} />
              Claim Your Free Session
              <ArrowRight size={24} />
            </button>
                 </Link>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }

        @media (max-width: 768px) {
          section {
            padding: 3rem 1rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default GoogleAdsPage;